import * as React from "react";
import { Navigate } from "react-router-dom";
import { toast } from "@eduviaos/ui-kit";

import { useEnabledModules } from "@/hooks/useEnabledModules";

/**
 * Phase F-finale-3 Slice 2 — `<RouteModuleGate>` is a route-level
 * defence layer for module-gated surfaces.
 *
 * Why we need it on top of sidebar gating
 * ---------------------------------------
 * The sidebar already hides disabled-module nav entries (Phase D0c).
 * But users still arrive at gated routes via deep links, the back/
 * forward stack, copy-pasted URLs from Slack, or a stale dashboard
 * link clicked while the modules payload was still loading. Without
 * a route guard the page renders its full chrome + empty data and
 * the user is left wondering what went wrong.
 *
 * Compared to `<PrerequisiteGate>` (Phase H4 FE-2) which surfaces a
 * configure-prerequisites landing when an *academic* catalogue gate
 * fails, this gate is much harder: a disabled module means "you
 * literally cannot use this area on this tenant" — there's no self-
 * service unblock. We bounce to `/dashboard` and explain via a toast.
 *
 * Loading window
 * --------------
 * We render `null` while `useEnabledModules` is still fetching. The
 * loading window is ~80–250ms on a warm cache; flashing the
 * protected child (or its loading skeleton) for that window would
 * leak module-gated UI to disabled tenants on every navigation. A
 * brief blank shell is the correct trade-off.
 *
 * Errors are treated as disabled — same posture as the BE
 * `require_module` dependency (Slice 4): when in doubt, deny.
 */
export interface RouteModuleGateProps {
  /**
   * Module key from `tenant.feature_flags.enabled_modules`. See the
   * 32-row catalogue in
   * `eduviaos-backend/app/domains/tenant/modules.py`.
   */
  module: string;
  children: React.ReactNode;
}

export function RouteModuleGate({ module, children }: RouteModuleGateProps) {
  const { isModuleEnabled, isLoading, isError } = useEnabledModules();

  if (isLoading) {
    return null;
  }

  if (!isError && isModuleEnabled(module)) {
    return <>{children}</>;
  }

  return <ModuleGateRedirect />;
}

function ModuleGateRedirect() {
  React.useEffect(() => {
    toast.error("This area isn't enabled for your institution.", {
      description: "Ask your account manager to enable the module if you need access.",
      duration: 4000,
    });
  }, []);
  return <Navigate to="/dashboard" replace />;
}
